const MAX_EMAIL_LENGTH = 254;

/** Affirm-allowed specials in the local part (dot placement checked separately). */
const ALLOWED_SPECIAL = new Set(['.', '+', '-', '_', '%']);

/**
 * Allowed characters in the local part (before @).
 * Follows Affirm's stricter rules — blocks: ! # $ & ' * / = ? ^ ` { | } ~
 * Allows: alphanumeric, plus (+), hyphen (-), underscore (_), percent (%), dot (.)
 * Dot has additional placement rules checked separately.
 */
function isAllowedLocalChar(char: string): boolean {
  return (
    (char >= 'a' && char <= 'z') ||
    (char >= 'A' && char <= 'Z') ||
    (char >= '0' && char <= '9') ||
    ALLOWED_SPECIAL.has(char)
  );
}

function isValidLocalPart(local: string): boolean {
  if (local.length === 0) return false;
  // Dot must not be first or last
  if (local.startsWith('.') || local.endsWith('.')) return false;

  let prevDot = false;
  for (const char of local) {
    if (char === '.') {
      // No consecutive dots
      if (prevDot) return false;
      prevDot = true;
    } else {
      prevDot = false;
      if (!isAllowedLocalChar(char)) return false;
    }
  }
  return true;
}

function containsWhitespaceOrAt(value: string): boolean {
  for (const char of value) {
    if (char === '@' || char === ' ' || char === '\t' || char === '\n' || char === '\r') {
      return true;
    }
  }
  return false;
}

/**
 * Validates email format (ReDoS-safe, O(n)).
 * Local part follows Affirm's stricter character set — blocks: ! # $ & ' * / = ? ^ ` { | } ~
 * Dot allowed in local part only if not first/last and not consecutive.
 */
export function isValidEmailAddress(email: string): boolean {
  const value = email.trim();
  if (value.length === 0 || value.length > MAX_EMAIL_LENGTH) {
    return false;
  }

  const atIndex = value.indexOf('@');
  if (atIndex < 1 || value.includes('@', atIndex + 1)) {
    return false;
  }

  const localPart = value.slice(0, atIndex);
  const domainPart = value.slice(atIndex + 1);

  if (!isValidLocalPart(localPart)) {
    return false;
  }

  if (
    domainPart.length === 0 ||
    domainPart.startsWith('.') ||
    !domainPart.includes('.') ||
    containsWhitespaceOrAt(domainPart)
  ) {
    return false;
  }

  const lastDotIndex = domainPart.lastIndexOf('.');
  return lastDotIndex >= 1 && lastDotIndex < domainPart.length - 1;
}
